import { useEffect, useState } from "react";
import { adminApi } from "../api";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  GripVertical,
} from "lucide-react";
import { resolveImage } from "../../lib/data";

/**
 * Investor Sections CMS — flexible "rich block" editor for the
 * investor area. Each section has: slug, eyebrow, title, body (plain
 * text w/ paragraphs and "- " bullets), and a list of {url, caption}
 * images (uploaded charts, organigrams, etc.).
 */

const INITIAL = {
  slug: "",
  title: "",
  eyebrow: "",
  body: "",
  images: [],
  order: 999,
  published: true,
};

export default function AdminInvestorSections() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await adminApi.get("/admin/investor-sections");
      setItems(r.data);
    } catch {
      toast.error("Errore caricamento sezioni");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm({ ...INITIAL, order: (items.length + 1) * 10 });
    setEditing("new");
  };

  const openEdit = (item) => {
    setForm({ ...INITIAL, ...item });
    setEditing(item.id);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        published: !!form.published,
        images: Array.isArray(form.images) ? form.images : [],
      };
      if (editing === "new") {
        await adminApi.post("/admin/investor-sections", payload);
        toast.success("Sezione creata");
      } else {
        await adminApi.put(`/admin/investor-sections/${editing}`, payload);
        toast.success("Sezione aggiornata");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la sezione?")) return;
    try {
      await adminApi.delete(`/admin/investor-sections/${id}`);
      toast.success("Eliminata");
      load();
    } catch {
      toast.error("Errore");
    }
  };

  return (
    <div data-testid="admin-investor-sections-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
        CMS · Area Investitori
      </div>
      <div className="mt-2 flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-display font-light text-5xl tracking-tightest">
          Sezioni Investitori
        </h1>
        <button
          onClick={openNew}
          data-testid="inv-section-new"
          className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm font-medium hover:bg-[#C48F56] transition-colors"
        >
          <Plus size={14} /> Nuova sezione
        </button>
      </div>
      <p className="mt-3 text-white/50 text-sm max-w-2xl">
        Sezioni testuali con immagini (grafici, tabelle, organigrammi)
        visibili nell'area investitori riservata. Esempi: "Profilo Societario",
        "Struttura Societaria", "Conto Economico", "Stato Patrimoniale".
      </p>

      <div className="mt-8 rounded-2xl border border-white/10 bg-[#0F0F14] overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-white/40">
            Nessuna sezione. Clicca "Nuova sezione" per iniziare.
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {items.map((item) => (
              <li
                key={item.id}
                className="p-5 flex items-center gap-4"
                data-testid={`inv-section-${item.slug}`}
              >
                <div className="text-white/30 font-mono text-xs w-8">
                  #{item.order}
                </div>
                {item.images?.[0]?.url && (
                  <img
                    src={resolveImage(item.images[0].url)}
                    alt=""
                    className="h-14 w-20 rounded-md object-cover border border-white/10"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg">{item.title}</div>
                  <div className="mt-1 text-xs text-white/50 font-mono uppercase tracking-[0.15em]">
                    {item.slug} · {item.images?.length || 0} immagini
                    {item.published === false && (
                      <span className="ml-3 text-red-400">BOZZA</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEdit(item)}
                    className="h-9 w-9 rounded-full border border-white/10 grid place-items-center hover:bg-white/5"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => remove(item.id)}
                    className="h-9 w-9 rounded-full border border-white/10 grid place-items-center hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing !== null && (
        <FormModal
          form={form}
          setForm={setForm}
          onClose={() => setEditing(null)}
          onSubmit={save}
          saving={saving}
          isNew={editing === "new"}
        />
      )}
    </div>
  );
}

function FormModal({ form, setForm, onClose, onSubmit, saving, isNew }) {
  const set = (k) => (e) => {
    const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((s) => ({ ...s, [k]: v }));
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-6 overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl bg-[#0A0A0D] border border-white/10 rounded-2xl p-8 my-10"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl tracking-tight">
            {isNew ? "Nuova sezione" : "Modifica sezione"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full border border-white/10 grid place-items-center hover:bg-white/5"
          >
            <X size={14} />
          </button>
        </div>

        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
          <TextField label="Slug (identificativo, non si modifica)" value={form.slug} onChange={set("slug")} required placeholder="es. profilo-societario" />
          <TextField label="Occhiello (uppercase piccolo)" value={form.eyebrow} onChange={set("eyebrow")} placeholder="Es. Gruppo Invest Hospitality" />
          <TextField label="Titolo" value={form.title} onChange={set("title")} required placeholder="Es. Invest Hospitality S.p.A." />
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
              Corpo (paragrafi separati da riga vuota · bullet con "- ")
            </label>
            <textarea
              value={form.body}
              onChange={set("body")}
              rows={10}
              className="w-full bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#C48F56] outline-none resize-y"
              placeholder="Invest Hospitality S.p.A. nasce nel 2014 come holding…

Il Gruppo concentra le proprie attività su Milano…

- Identificazione della zona
- Scelta della location
- Negoziazione del prezzo"
            />
          </div>
          <ImagesEditor
            images={form.images}
            onChange={(imgs) => setForm((s) => ({ ...s, images: imgs }))}
          />
          <div className="grid grid-cols-2 gap-5">
            <TextField label="Ordine" type="number" value={form.order} onChange={set("order")} />
            <label className="flex items-center gap-3 cursor-pointer self-end pb-2">
              <input
                type="checkbox"
                checked={!!form.published}
                onChange={set("published")}
                className="h-4 w-4 accent-[#C48F56]"
              />
              <span className="text-sm">Pubblicata</span>
            </label>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-full border border-white/15 px-5 py-2 text-sm">
            Annulla
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2 text-sm font-medium hover:bg-[#C48F56] disabled:opacity-50"
          >
            {saving ? "Salvataggio…" : "Salva"} <Save size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}

function TextField({ label, value, onChange, required, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-2 text-sm"
      />
    </div>
  );
}

function ImagesEditor({ images, onChange }) {
  const list = Array.isArray(images) ? images : [];
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const next = [...list];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await adminApi.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push({ url: r.data.url, caption: "" });
      }
      onChange(next);
      toast.success(`${files.length} immagini caricate`);
    } catch (err) {
      toast.error("Upload fallito");
    } finally {
      setUploading(false);
    }
  };

  const updateCaption = (i, caption) => {
    const next = [...list];
    next[i] = { ...next[i], caption };
    onChange(next);
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const remove = (i) => {
    onChange(list.filter((_, idx) => idx !== i));
  };

  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
        Immagini (grafici / tabelle / organigrammi)
      </label>
      <div className="space-y-2">
        {list.map((img, i) => (
          <div
            key={i}
            className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-black/30"
          >
            <button
              type="button"
              onClick={() => move(i, -1)}
              disabled={i === 0}
              className="text-white/30 hover:text-white disabled:opacity-20"
              title="Sposta su"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => move(i, 1)}
              disabled={i === list.length - 1}
              className="text-white/30 hover:text-white disabled:opacity-20"
              title="Sposta giù"
            >
              ↓
            </button>
            <img
              src={resolveImage(img.url)}
              alt=""
              className="h-14 w-20 rounded-md object-cover border border-white/10"
            />
            <input
              type="text"
              value={img.caption || ""}
              onChange={(e) => updateCaption(i, e.target.value)}
              placeholder="Didascalia (opzionale)"
              className="flex-1 bg-transparent border-b border-white/10 focus:border-[#C48F56] outline-none py-1 text-sm"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="h-9 w-9 grid place-items-center rounded-full hover:bg-red-500/10 hover:text-red-400 text-white/50"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <label className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-white/15 hover:border-[#C48F56]/50 cursor-pointer transition-colors">
          <Upload size={14} className="text-white/50" />
          <span className="text-sm text-white/70">
            {uploading ? "Caricamento…" : "Carica una o più immagini"}
          </span>
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={upload}
            disabled={uploading}
          />
        </label>
      </div>
    </div>
  );
}
